import { ActionTree } from 'vuex';
import ApiUrl from '../../../../api/url';
import { ApplicationTemplateState, NewApplicationTemplate } from './types';
import { RootState } from '../../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../../error-type';
import { AppContext } from '../../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../../translator';

export const actions: ActionTree<ApplicationTemplateState, RootState> = {
    /**
     * 業務システム紐付き申請書テンプレート一覧取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id 業務システムID
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソートするカラムと順番
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { id, pageNumber, perPage, sort } = {}) {
        const payload = {
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
            routine_work_enabled: false,
        };
        const templateId = id;
        try {
            commit(types.INDEX_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.BusinessSystem.ApplicationTemplate.INDEX.replace(':id', templateId.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.INDEX_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.INDEX_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 業務システム紐付き申請書テンプレート新規作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {string} id 業務システムID
     * @param {NewApplicationTemplate} newApplicationTemplate 新規申請書テンプレート情報
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, { id, newApplicationTemplate } = {}) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.BusinessSystem.ApplicationTemplate.CREATE.replace(':id', id),
                    params: { template: newApplicationTemplate },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.BusinessSystem.ApplicationTemplate.CREATED);
                dispatch('toastMessage', `${newApplicationTemplate.name} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * 業務システム紐付き申請書テンプレート詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} businessSystemId 業務システムID
     * @param {any} templateId 申請書テンプレートID
     */
    async show({ dispatch, commit }, { businessSystemId, templateId }) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.BusinessSystem.ApplicationTemplate.SHOW.replace(
                        ':id',
                        businessSystemId.toString()
                    ).replace(':template_id', templateId.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 申請書テンプレート更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} businessSystemId 業務システムID
     * @param {any} templateId 申請書テンプレートID
     * @param {any} application_template 申請書テンプレート
     * @param {any} isRoutineWorkTemplate 定常作業テンプレートかどうか（True:定常作業テンプレート,False:申請書テンプレート）
     * @returns {Promise<void>}
     */
    async update(
        { dispatch, commit, rootState },
        { businessSystemId, templateId, application_template, isRoutineWorkTemplate } = {}
    ) {
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.BusinessSystem.ApplicationTemplate.UPDATE.replace(
                        ':id',
                        businessSystemId.toString()
                    ).replace(':template_id', templateId.toString()),
                    params: { template: application_template },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const messageKey = isRoutineWorkTemplate
                    ? MessageKey.BusinessSystem.RoutineWorkTemplate.UPDATED
                    : MessageKey.BusinessSystem.ApplicationTemplate.UPDATED;
                const message = translator(i18n, messageKey);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * 申請書テンプレート削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id 業務システムID
     * @param {any} templateId 申請書テンプレートID
     * @param {any} isRoutineWorkTemplate 定常作業テンプレートかどうか（True:定常作業テンプレート,False:申請書テンプレート）
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, { id, templateId, isRoutineWorkTemplate } = {}) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.BusinessSystem.ApplicationTemplate.DESTROY.replace(':id', id.toString()).replace(
                        ':template_id',
                        templateId.toString()
                    ),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const messageKey = isRoutineWorkTemplate
                    ? MessageKey.BusinessSystem.RoutineWorkTemplate.DESTROYED
                    : MessageKey.BusinessSystem.ApplicationTemplate.DESTROYED;
                const message = translator(i18n, messageKey);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
